import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedTrips, INextAndPreviousTrip, IPreExchangeOperationRequest, IPreExchangeOperationResponse, IBookingResponse, IDeterminePassenger, ISelectedPassenger } from "../../../Orchestrator/Types/Index";
import { postPreExchangeOperation } from "../../../app/api/exchangeOffer";
import { getBooking } from "../../../app/api/getBooking";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { preSelectAddonsForExchangeAction } from "../Addons/PreSelectAddonsForExchangeAction";
import { ICalculatePassengerListByPriceActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookItAgainActionParams";
import { calculatePassengerListByPriceAction } from "../Search/CalculatePassengerListByPriceAction";
import { _eventBus } from "../../../core/events/EventBus";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { determinePassengerType } from "../../../Lib/Utils/validationUtil";
import * as Sentry from "@sentry/nextjs";

export class StartPreExchangeOperationAction extends BaseAction<IDefaultActionResult,
    { exchangeOfferId: string[], exchangeOfferPassengerRefs?: Array<{offerId: string, passengerRefs: string[]}>, nextTrainTicket: IBookedTrips, selectedPassengerForCancel: string[], selectedPassengerForCancelInBound: string[], changeTripAlreadyPaidAddons: any }> {
    override async run(): Promise<IDefaultActionResult> {
        let result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        try {
            const { exchangeOfferId, exchangeOfferPassengerRefs, nextTrainTicket, selectedPassengerForCancel, selectedPassengerForCancelInBound, changeTripAlreadyPaidAddons } = this.params!;
            if (exchangeOfferId.length) {
                const normalizedOfferIds = Array.from(new Set((exchangeOfferId as (string | string[])[]).flatMap((id) => Array.isArray(id) ? id : [id])));
                updateRecoilState<string[]>("exchangeOffersAtom", normalizedOfferIds)

                // ERM infant–adult linking: exchange-operations must reference the
                // WHOLE linked group — lap-infant refs stay with their adult (the
                // backend rejects group-incomplete operations).
                let exchangeOffersEntries: Array<{offerId: string, placeSelections: null, passengerRefs: string[], throughTicketTags: null}>;

                if (exchangeOfferPassengerRefs && exchangeOfferPassengerRefs.length > 0) {
                    // Per-pax INDIVIDUAL offers: use each offer's own passengerRefs
                    exchangeOffersEntries = exchangeOfferPassengerRefs
                        .map(({ offerId, passengerRefs }) => ({
                            offerId,
                            placeSelections: null,
                            passengerRefs,
                            throughTicketTags: null
                        }))
                        .filter(entry => entry.passengerRefs.length > 0);
                    // Fallback: if all per-offer refs were empty (POOL mode with no
                    // passengerRefs set), revert to scatter mode so the API receives
                    // a valid non-empty payload.
                    if (exchangeOffersEntries.length === 0 && nextTrainTicket.passengers.length > 0) {
                        console.warn(
                            "[StartPreExchangeOperationAction] All per-offer passengerRefs were empty — falling back to scatter mode"
                        );
                        exchangeOffersEntries = normalizedOfferIds.map((offerId: string) => ({
                            offerId,
                            placeSelections: null,
                            passengerRefs: nextTrainTicket.passengers.map(p => p.externalRef),
                            throughTicketTags: null
                        }));
                    }
                } else {
                    // Fallback: one entry per offer ID, all passengers incl. lap infants
                    exchangeOffersEntries = normalizedOfferIds.map((offerId: string) => ({
                        offerId,
                        placeSelections: null,
                        passengerRefs: nextTrainTicket.passengers.map(p => p.externalRef),
                        throughTicketTags: null
                    }));
                }

                const payloadPreExchangeOperation: IPreExchangeOperationRequest = {
                    exchangeOffers: exchangeOffersEntries
                }
                const preExchangeResponse: any = await postPreExchangeOperation(payloadPreExchangeOperation, nextTrainTicket.bookingId)
                if (preExchangeResponse.status === 'ok' && preExchangeResponse.answer) {
                    const bookingResponse: IPreExchangeOperationResponse = preExchangeResponse.answer;
                    if (bookingResponse.exchangeOperation) {
                        const exchangeOperationId = bookingResponse.exchangeOperation.id;
                        sessionStorage.setItem("exchangeOperationId", exchangeOperationId);
                        sessionStorage.setItem("exchangeBookingId", nextTrainTicket.bookingId)
                        //Get Booking by Booking Id
                        const getBookingResponse: any = await getBooking(nextTrainTicket.bookingId);
                        if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                            const bookingResponse: IBookingResponse = getBookingResponse.answer;
                            // Update Recoil state with the booking response
                            updateRecoilState('quickExchangeBookingResponseAtom', bookingResponse);
                            updateRecoilState('bookingResponseAtom', bookingResponse);

                            const passengerList: ISelectedPassenger[] = bookingResponse.booking?.passengers.map(passenger => {
                                const passengerObject: ISelectedPassenger = {
                                    id: passenger.id,
                                    firstName: passenger?.detail?.firstName || "",
                                    lastName: passenger?.detail?.lastName || "",
                                    email: passenger?.detail?.email || "",
                                    mobile: "",
                                    remember: false,
                                    age: passenger.age,
                                    seatingDetails: null,
                                    cards: passenger.cards || [],
                                    prmNeeds: passenger.prmNeeds
                                }
                                return passengerObject
                            });
                            let filteredPassengers: ISelectedPassenger[] = [];
                            const uniqueSelectedIds = Array.from(new Set([
                                ...selectedPassengerForCancel,
                                ...selectedPassengerForCancelInBound
                            ]));
                            if (uniqueSelectedIds.length > 0) {
                                filteredPassengers = passengerList.filter(p => uniqueSelectedIds.includes(p.id));
                            }

                            const passengersObject: { [key: string]: number } = {};
                            filteredPassengers.forEach((passenger) => {
                                const determinePassenger: IDeterminePassenger = {
                                    age: passenger?.age,
                                    cards: passenger?.cards || [],
                                    prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                                }
                                const passengerType = determinePassengerType(determinePassenger);

                                // Initialize the count for the passenger type if not already present
                                if (!passengersObject[passengerType]) {
                                    passengersObject[passengerType] = 0;
                                }

                                // Increment the count for this passenger type
                                passengersObject[passengerType] += 1;
                            });

                            updateRecoilState("searchStateAtom", { passengers: passengersObject });


                            const filteredBookingPassengers = bookingResponse.booking?.passengers.filter(p => uniqueSelectedIds.includes(p.id)) || [];
                            updateRecoilState("passengerStateAtom", null)
                            updateRecoilState("passengerStateAtom", filteredBookingPassengers);
                            // CDT addon pre-selection: auto-attach the original addons to the new
                            // exchange offer so the credit banner shows AED 0 when prices/products
                            // match. Failures are non-fatal — the existing summary path still runs.
                            preSelectAddonsForExchangeAction.setParams({
                                exchangeOperationId,
                                selectedPassengerForCancel,
                                selectedPassengerForCancelInBound,
                            });
                            await preSelectAddonsForExchangeAction.execute();
                            // preSelectAddonsForExchangeAction refreshes bookingResponseAtom
                            // with the newly-attached ancillaries; use that snapshot for the
                            // summary so otherDetails.addons reflects them. The local
                            // `bookingResponse` captured above is pre-POST and would show
                            // zero extras (which then shows the full credit on the banner).
                            const refreshedBookingResponse =
                                getRecoilStateValue<IBookingResponse>("bookingResponseAtom") || bookingResponse;
                            const bookingForSummary = refreshedBookingResponse.booking;
                            prepareBookingSummaryAction.setParams({
                                ...bookingForSummary,
                                isFrom: "quick-exchange",
                                isChangeDate: true,
                                selectedPassengerForCancel: selectedPassengerForCancel,
                                selectedPassengerForCancelInBound: selectedPassengerForCancelInBound,
                                changeTripAlreadyPaidAddons: changeTripAlreadyPaidAddons
                            })
                            await prepareBookingSummaryAction.execute()

                            const params: ICalculatePassengerListByPriceActionParams = {
                                booking: bookingForSummary,
                                screen: "",
                                passengerSpecificationsTypes: null
                            }
                            await calculatePassengerListByPriceAction.setParams(params)
                            await calculatePassengerListByPriceAction.execute();

                            updateRecoilState("paymentStateAtom", {
                                // Left unset so <Payment> can preselect the customer's default saved
                                // card — it only preselects while no method is set (Payment.tsx).
                                method: null,
                                total: nextTrainTicket.outboundConfirmedPrice,
                            });

                            updateRecoilState("isQuickExchangeStateAtom", true);
                            updateRecoilState("isChangeDateStateAtom", true);
                            result.status = "success"
                        }
                    }
                }
            }
        } catch (error: any) {
            console.error("Error in StartPreExchangeOperationAction:", error);
            Sentry.captureException(error, { tags: { action: "StartPreExchangeOperationAction" } });
        } finally {
            return result;
        }
    }
}

export const startPreExchangeOperationAction = new StartPreExchangeOperationAction()